import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type EquipmentCatalogue,
  type OrganisationEquipmentType,
  type ReplaceOrganisationEquipmentTypesRequest,
} from '../client';

/**
 * Stable query-key factory for the static equipment catalogue. Single
 * top-level key — the catalogue is the same for every tenant, so there's no
 * tenant or org slot to encode.
 */
export const equipmentCatalogueKeys = {
  all: ['directory', 'equipment-catalogue'] as const,
};

/**
 * Stable query-key factory for the per-org equipment-type selection caches.
 * Scoped by `organisationId` so switching orgs in the TenantSwitcher always
 * refetches.
 */
export const organisationEquipmentTypeKeys = {
  all: ['directory', 'organisation-equipment-type'] as const,
  byOrganisation: (organisationId: string) =>
    ['directory', 'organisation-equipment-type', 'org', organisationId] as const,
};

/**
 * Fetch the static equipment catalogue (Trailer + Rigid groups + types).
 *
 * Cached aggressively on the FE — the catalogue keys are append-only by BE
 * convention, so a 24h `staleTime` + Infinity `gcTime` is fine; even when an
 * admin opens the picker dialog twice the second open is instant. If the FE
 * ever needs to force a fresh pull (e.g. after a BE deploy that adds new
 * types), consume the React Query devtools / call `refetch()` explicitly.
 */
export function useEquipmentCatalogue(
  options?: Omit<UseQueryOptions<EquipmentCatalogue, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<EquipmentCatalogue, Error>({
    queryKey: equipmentCatalogueKeys.all,
    queryFn: () => directoryApi.getEquipmentCatalogue(),
    staleTime: 24 * 60 * 60 * 1000, // 24h — the catalogue rarely changes.
    gcTime: Infinity,
    ...options,
  });
}

/**
 * Fetch every active equipment-type selection for an organisation (across
 * both Trailer and Rigid classes). Pass `null` for `organisationId` to keep
 * the query disabled, e.g. while the active org is still being resolved.
 */
export function useOrganisationEquipmentTypes(
  organisationId: string | null,
  options?: Omit<
    UseQueryOptions<OrganisationEquipmentType[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<OrganisationEquipmentType[], Error>({
    queryKey: organisationId
      ? organisationEquipmentTypeKeys.byOrganisation(organisationId)
      : organisationEquipmentTypeKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getOrganisationEquipmentTypes(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Mutation wrapper around PUT `/organisationEquipmentTypes`. The picker
 * dialog calls this once per save with the new full selection for ONE class;
 * the BE diffs and returns the resulting active rows for that class only.
 *
 * Invalidates the per-org selection cache on success so any other consumers
 * (e.g. the Resources form) refetch and pick up the change.
 */
export function useReplaceOrganisationEquipmentTypes() {
  const qc = useQueryClient();
  return useMutation<
    OrganisationEquipmentType[],
    Error,
    ReplaceOrganisationEquipmentTypesRequest
  >({
    mutationKey: [...organisationEquipmentTypeKeys.all, 'replace'],
    mutationFn: (body) => directoryApi.replaceOrganisationEquipmentTypes(body),
    onSuccess: (_rows, vars) => {
      qc.invalidateQueries({
        queryKey: organisationEquipmentTypeKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}
